import * as React from 'react'
import clx from 'classnames'
import { CheckIcon } from '../../app/features/icons/Check'

export type Step = {
  id?: string | number
  title: string
  status?: 'completed' | 'active' | 'upcoming'
  children?: Step[]
}

type Props = {
  steps: Step[]
  className?: string
  isSublist?: boolean
  isChildOfLastStep?: boolean
  onStepClick?: (id: string | number) => void
}

const StepperComponent: React.FC<Props> = ({
  steps,
  className,
  isSublist = false,
  isChildOfLastStep,
  onStepClick,
}) => {
  return (
    <ol className={clx('tw-flex tw-flex-col tw-gap-2.5', className)}>
      {steps.map((step, index) => {
        const isLastStep = !isSublist && index === steps.length - 1
        const isLastChildOfLastStep =
          isSublist && isChildOfLastStep && index === steps.length - 1

        const showLine =
          (!isLastStep && !isLastChildOfLastStep) ||
          (isLastStep && step.status === 'active' && !isLastChildOfLastStep)

        const isClickable =
          onStepClick != null &&
          step.id != null &&
          (step.status === 'completed' || step.status === 'active')

        const handleClick = () => {
          if (isClickable && step.id != null) onStepClick!(step.id)
        }

        return (
          <React.Fragment key={step.id ?? index}>
            <li
              className={clx(
                'tw-relative tw-flex tw-items-center tw-gap-3',
                isClickable && 'tw-cursor-pointer',
              )}
              onClick={isClickable ? handleClick : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={(event) => {
                if (!isClickable) return
                if (event.key === 'Enter' || event.key === ' ') {
                  event.preventDefault()
                  handleClick()
                }
              }}
            >
              {showLine && (
                <span
                  className={clx(
                    'tw-absolute tw-left-[5px] tw-top-5 tw-h-9 tw-border-l',
                    step.status === 'completed' ||
                      (step.status === 'active' && step.children)
                      ? 'tw-border-focus-100'
                      : 'tw-border-base-300',
                  )}
                />
              )}
              <span
                className={clx(
                  'tw-relative tw-z-10',
                  step.status === 'upcoming' ? 'tw-py-0.5' : 'tw-py-1',
                )}
              >
                <span
                  className={clx(
                    'tw-flex tw-h-3 tw-w-3 tw-items-center tw-justify-center tw-rounded-full tw-border',
                    step.status === 'completed'
                      ? `tw-bg-focus-100 ${isSublist ? 'tw-border-[3px] tw-border-content-primary' : 'tw-border-focus-100'}`
                      : step.status === 'active'
                        ? `tw-bg-focus-100 ${isSublist ? 'tw-border-[3px] tw-border-content-primary' : 'tw-border-focus-100'}`
                        : `tw-border-base-300 tw-bg-base-300 ${isSublist ? 'tw-border-[3px]' : 'tw-border-2'}`,
                  )}
                >
                  {step.status === 'completed' && !isSublist && (
                    <CheckIcon className="tw-h-3 tw-w-3 tw-text-white" />
                  )}
                </span>
              </span>

              <div
                className={clx(
                  'tw-flex tw-min-h-7 tw-items-center',
                  step.status === 'completed'
                    ? 'tw-text-xs tw-text-content-base'
                    : step.status === 'active'
                      ? 'tw-text-sm tw-font-medium tw-text-content-base'
                      : 'tw-text-xs tw-text-content-base/40',
                  {
                    'tw-text-[11px]': isSublist,
                    'tw-w-36': !isSublist && step.status === 'active',
                    'tw-w-28': !isSublist && step.status !== 'active',
                  },
                  isClickable && 'hover:tw-underline',
                )}
              >
                <p>{step.title}</p>
              </div>
            </li>
            {step.children && step.status === 'active' && (
              <StepperComponent
                steps={step.children}
                isSublist={true}
                isChildOfLastStep={isLastStep}
                onStepClick={onStepClick}
              />
            )}
          </React.Fragment>
        )
      })}
    </ol>
  )
}

export const Stepper = React.memo(StepperComponent) as React.FC<Props>
